'use client';

import { c as _c } from "react/compiler-runtime";
import { Minus, Plus } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '~/components/Button';
import { OverlayDrawer } from '~/components/Drawer';
import { Select } from '~/components/Select';
import { AnimatedPopoverContent } from '~/components/TripOverview/AnimatedPopoverContent';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { minNumberRooms } from '~/utils/paxHelpers';
import styles from './ChangeTravellersModal.module.css';
type ChangeTravellersModalProps = {
  isOpen: boolean;
  close: () => void;
  adultCount: number;
  initialChildAges: number[];
  rooms: number;
  tripEndDateLabel: string;
  onConfirm: (selection: {
    adults: number;
    children: number;
    rooms: number;
    childAges: number[];
  }) => Promise<boolean>;
  presentation?: 'modal' | 'popover';
};
type TravellerBreakdown = {
  adults: number;
  children: number;
};
const MIN_COUNT = 0;
const MIN_ADULTS = 1;
const MAX_ROOMS = 6;
const MAX_ADULTS = MAX_ROOMS * 2;
const MIN_CHILD_AGE = 1;
const MAX_CHILD_AGE = 17;
const CHILD_AGE_OPTIONS = Array.from({
  length: MAX_CHILD_AGE - MIN_CHILD_AGE + 1
}, (_, index) => index + MIN_CHILD_AGE);
const normalizeChildAges = (ages: number[], childCount: number): (number | '')[] => {
  const normalized = ages.slice(0, childCount).map(age => age >= MIN_CHILD_AGE && age <= MAX_CHILD_AGE ? age : '');
  while (normalized.length < childCount) {
    normalized.push('');
  }
  return normalized;
};
const areChildAgesComplete = (ages: (number | '')[], childCount: number) => childCount === 0 || ages.slice(0, childCount).every(age => typeof age === 'number');
export const ChangeTravellersModal = t0 => {
  const $ = _c(108);
  const {
    isOpen,
    close,
    adultCount,
    initialChildAges,
    rooms,
    tripEndDateLabel,
    onConfirm,
    presentation: t1
  } = t0;
  const presentation = t1 === undefined ? "modal" : t1;
  const t = useTranslations("journey");
  const initialChildren = initialChildAges.length;
  const t2 = Math.max(MIN_ADULTS, adultCount);
  let t3;
  if ($[0] !== initialChildren || $[1] !== t2) {
    t3 = {
      adults: t2,
      children: initialChildren
    };
    $[0] = initialChildren;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [breakdown, setBreakdown] = useState(t3);
  let t4;
  if ($[3] !== initialChildAges || $[4] !== initialChildren) {
    t4 = () => normalizeChildAges(initialChildAges, initialChildren);
    $[3] = initialChildAges;
    $[4] = initialChildren;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [childAges, setChildAges] = useState(t4);
  const [roomCount, setRoomCount] = useState(rooms);
  const isTripUpdating = useIsTripUpdating();
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      const children = initialChildAges.length;
      setBreakdown({
        adults: Math.max(MIN_ADULTS, adultCount),
        children
      });
      setChildAges(normalizeChildAges(initialChildAges, children));
      setRoomCount(rooms);
    }
  }
  let t5;
  if ($[6] !== breakdown.adults || $[7] !== childAges) {
    const numericChildAges = childAges.filter(_temp);
    t5 = minNumberRooms(breakdown.adults, numericChildAges);
    $[6] = breakdown.adults;
    $[7] = childAges;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const minRooms = t5;
  const effectiveRoomCount = Math.min(Math.max(roomCount, minRooms), MAX_ROOMS);
  let t6;
  if ($[9] !== breakdown.children || $[10] !== childAges) {
    t6 = areChildAgesComplete(childAges, breakdown.children);
    $[9] = breakdown.children;
    $[10] = childAges;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const canConfirm = t6;
  let t7;
  if ($[12] !== t || $[13] !== tripEndDateLabel) {
    t7 = t("booking_child_age_on_date", {
      date: tripEndDateLabel
    });
    $[12] = t;
    $[13] = tripEndDateLabel;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const childAgePlaceholder = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    const syncChildAges = nextChildCount => {
      setChildAges(current => normalizeChildAges(current.filter(_temp2) as number[], nextChildCount));
    };
    t8 = (key, delta) => {
      setBreakdown(current_0 => {
        const minForKey = key === "adults" ? MIN_ADULTS : MIN_COUNT;
        const nextValue = Math.max(minForKey, current_0[key] + delta);
        const nextBreakdown = {
          ...current_0,
          [key]: nextValue
        };
        if (key === "children") {
          syncChildAges(nextValue);
        }
        return nextBreakdown;
      });
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const updateCounter = t8;
  let t9;
  if ($[16] !== effectiveRoomCount || $[17] !== minRooms) {
    t9 = () => setRoomCount(Math.max(minRooms, effectiveRoomCount - 1));
    $[16] = effectiveRoomCount;
    $[17] = minRooms;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const decrementRooms = t9;
  let t10;
  if ($[19] !== effectiveRoomCount) {
    t10 = () => setRoomCount(Math.min(MAX_ROOMS, effectiveRoomCount + 1));
    $[19] = effectiveRoomCount;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const incrementRooms = t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (index, value) => {
      const parsed = Number.parseInt(value, 10);
      setChildAges(current_1 => {
        const next = [...current_1];
        next[index] = Number.isNaN(parsed) ? "" : parsed;
        return next;
      });
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleChildAgeChange = t11;
  let t12;
  if ($[22] !== breakdown.adults || $[23] !== breakdown.children || $[24] !== canConfirm || $[25] !== childAges || $[26] !== close || $[27] !== effectiveRoomCount || $[28] !== onConfirm) {
    t12 = async () => {
      if (!canConfirm) {
        return;
      }
      const confirmedChildAges = childAges.slice(0, breakdown.children).filter(_temp3);
      const success = await onConfirm({
        adults: breakdown.adults,
        children: breakdown.children,
        rooms: effectiveRoomCount,
        childAges: confirmedChildAges
      });
      if (success) {
        close();
      }
    };
    $[22] = breakdown.adults;
    $[23] = breakdown.children;
    $[24] = canConfirm;
    $[25] = childAges;
    $[26] = close;
    $[27] = effectiveRoomCount;
    $[28] = onConfirm;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const handleConfirm = t12;
  let t13;
  if ($[30] !== t) {
    t13 = t("tab_travellers");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <h3 className={styles.sectionHeading}>{t13}</h3>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className={styles.divider} aria-hidden={true} />;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("booking_adult_label");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("pax_adults_age");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => updateCounter("adults", -1);
    t19 = () => updateCounter("adults", 1);
    $[39] = t18;
    $[40] = t19;
  } else {
    t18 = $[39];
    t19 = $[40];
  }
  let t20;
  if ($[41] !== breakdown.adults || $[42] !== isTripUpdating || $[43] !== t16 || $[44] !== t17) {
    t20 = <CounterRow label={t16} detail={t17} value={breakdown.adults} min={MIN_ADULTS} max={MAX_ADULTS} isDisabled={isTripUpdating} onDecrement={t18} onIncrement={t19} />;
    $[41] = breakdown.adults;
    $[42] = isTripUpdating;
    $[43] = t16;
    $[44] = t17;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t) {
    t21 = t("booking_child_label");
    $[46] = t;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("booking_child_age");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = breakdown.children === 0;
  let t24;
  let t25;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => updateCounter("children", -1);
    t25 = () => updateCounter("children", 1);
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  let t26;
  if ($[52] !== breakdown.children || $[53] !== isTripUpdating || $[54] !== t21 || $[55] !== t22 || $[56] !== t23) {
    t26 = <CounterRow label={t21} detail={t22} value={breakdown.children} min={MIN_COUNT} isDisabled={isTripUpdating} hasBorder={t23} onDecrement={t24} onIncrement={t25} />;
    $[52] = breakdown.children;
    $[53] = isTripUpdating;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== breakdown.children || $[59] !== childAgePlaceholder || $[60] !== childAges || $[61] !== isTripUpdating || $[62] !== t) {
    t27 = breakdown.children > 0 ? <div className={styles.childAgeFields}>{childAges.slice(0, breakdown.children).map((age_2, index_0) => <div key={`child-age-field-${index_0}`} className={styles.childAgeSelectField}><Select id={`trip-child-age-${index_0}`} name={`child-age-${index_0}`} required={true} label={t("booking_child_age_select_label", {
          index: index_0 + 1
        })} labelVariant="small" selected={age_2 === "" ? "" : String(age_2)} disabled={isTripUpdating} onChange={event => handleChildAgeChange(index_0, event.target.value)}><option value="" disabled={true}>{childAgePlaceholder}</option>{CHILD_AGE_OPTIONS.map(_temp4)}</Select></div>)}</div> : null;
    $[58] = breakdown.children;
    $[59] = childAgePlaceholder;
    $[60] = childAges;
    $[61] = isTripUpdating;
    $[62] = t;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t14 || $[65] !== t20 || $[66] !== t26 || $[67] !== t27) {
    t28 = <div className={styles.section}>{t14}{t15}{t20}{t26}{t27}</div>;
    $[64] = t14;
    $[65] = t20;
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t) {
    t29 = t("booking_bedroom_preferences_heading");
    $[69] = t;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t29) {
    t30 = <h3 className={styles.sectionHeading}>{t29}</h3>;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className={styles.divider} aria-hidden={true} />;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t) {
    t32 = t("booking_rooms_label");
    $[74] = t;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t) {
    t33 = t("booking_rooms_capacity_hint");
    $[76] = t;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== decrementRooms || $[79] !== effectiveRoomCount || $[80] !== incrementRooms || $[81] !== isTripUpdating || $[82] !== minRooms || $[83] !== t32 || $[84] !== t33) {
    t34 = <CounterRow label={t32} detail={t33} value={effectiveRoomCount} min={minRooms} max={MAX_ROOMS} isDisabled={isTripUpdating} onDecrement={decrementRooms} onIncrement={incrementRooms} />;
    $[78] = decrementRooms;
    $[79] = effectiveRoomCount;
    $[80] = incrementRooms;
    $[81] = isTripUpdating;
    $[82] = minRooms;
    $[83] = t32;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t30 || $[87] !== t34) {
    t35 = <div className={styles.section}>{t30}{t31}{t34}</div>;
    $[86] = t30;
    $[87] = t34;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  const t36 = isTripUpdating || !canConfirm;
  let t37;
  if ($[89] !== t) {
    t37 = t("confirm");
    $[89] = t;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== handleConfirm || $[92] !== t36 || $[93] !== t37) {
    t38 = <div className={styles.confirmButtonShell}><Button variant="primary" size="fullWidth" isDisabled={t36} onPress={handleConfirm}>{t37}</Button></div>;
    $[91] = handleConfirm;
    $[92] = t36;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t28 || $[96] !== t35 || $[97] !== t38) {
    t39 = <div className={styles.modalRoot}>{t28}{t35}{t38}</div>;
    $[95] = t28;
    $[96] = t35;
    $[97] = t38;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  const content = t39;
  if (presentation === "popover") {
    if (!isOpen) {
      return null;
    }
    let t40;
    if ($[99] !== content) {
      t40 = <AnimatedPopoverContent viewKey="travellers" className={styles.popoverContent}>{content}</AnimatedPopoverContent>;
      $[99] = content;
      $[100] = t40;
    } else {
      t40 = $[100];
    }
    return t40;
  }
  let t40;
  if ($[101] !== t) {
    t40 = t("booking_choose_travellers");
    $[101] = t;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  if ($[103] !== close || $[104] !== content || $[105] !== isOpen || $[106] !== t40) {
    t41 = <OverlayDrawer isOpen={isOpen} close={close} ariaLabel={t40} dataTestId="ChangeTravellersDrawer">{content}</OverlayDrawer>;
    $[103] = close;
    $[104] = content;
    $[105] = isOpen;
    $[106] = t40;
    $[107] = t41;
  } else {
    t41 = $[107];
  }
  return t41;
};
type CounterRowProps = {
  label: string;
  detail: string;
  value: number;
  min: number;
  max?: number;
  isDisabled?: boolean;
  hasBorder?: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
};
const CounterRow = t0 => {
  const $ = _c(33);
  const {
    label,
    detail,
    value,
    min,
    max,
    isDisabled,
    hasBorder: t1,
    onDecrement,
    onIncrement
  } = t0;
  const hasBorder = t1 === undefined ? true : t1;
  const t = useTranslations("journey");
  const canDecrement = value > min;
  const canIncrement = max === undefined || value < max;
  const t2 = hasBorder ? styles.counterRow : styles.counterRowNoBorder;
  let t3;
  if ($[0] !== label) {
    t3 = <p className={styles.counterLabel}>{label}</p>;
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== detail) {
    t4 = <p className={styles.counterDetail}>{detail}</p>;
    $[2] = detail;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <div className={styles.counterText}>{t3}{t4}</div>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !canDecrement || isDisabled;
  let t7;
  if ($[7] !== label || $[8] !== t) {
    t7 = t("booking_decrease_counter", {
      label
    });
    $[7] = label;
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Minus decorative={true} size="small" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== onDecrement || $[12] !== t6 || $[13] !== t7) {
    t9 = <button type="button" className={styles.controlButton} disabled={t6} onClick={onDecrement} aria-label={t7}>{t8}</button>;
    $[11] = onDecrement;
    $[12] = t6;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== value) {
    t10 = <span className={styles.value}>{value}</span>;
    $[15] = value;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !canIncrement || isDisabled;
  let t12;
  if ($[17] !== label || $[18] !== t) {
    t12 = t("booking_increase_counter", {
      label
    });
    $[17] = label;
    $[18] = t;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Plus decorative={true} size="small" />;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== onIncrement || $[22] !== t11 || $[23] !== t12) {
    t14 = <button type="button" className={styles.controlButton} disabled={t11} onClick={onIncrement} aria-label={t12}>{t13}</button>;
    $[21] = onIncrement;
    $[22] = t11;
    $[23] = t12;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t10 || $[26] !== t14 || $[27] !== t9) {
    t15 = <div className={styles.controls}>{t9}{t10}{t14}</div>;
    $[25] = t10;
    $[26] = t14;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t2 || $[31] !== t5) {
    t16 = <div className={t2}>{t5}{t15}</div>;
    $[29] = t15;
    $[30] = t2;
    $[31] = t5;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
function _temp(age) {
  return typeof age === "number";
}
function _temp2(age_0) {
  return age_0 !== "";
}
function _temp3(age_1) {
  return typeof age_1 === "number";
}
function _temp4(age_3) {
  return <option key={age_3} value={age_3}>{age_3}</option>;
}